import { gridSpacing } from 'constants/themeConstants';
import { Alert, AlertTitle } from '@mui/material';

import { DatagridNames } from 'types/datagrid';
import ExtendedDatagrid from 'ui-component/DataGrid/ExtendedDatagrid';
import { useGetCustomPropertiesQuery } from 'store/slices/apiV1/org';
import { GridValueGetterParams } from '@mui/x-data-grid';
import {
  CustomPropertyDataTypeDisplay,
  CustomPropertyModelDisplay,
  CustomPropertyVisibilityDisplay,
} from 'types/customProperty';
import _ from 'lodash';
import { longTextWithEllipsisRenderCell } from 'ui-component/DataGrid/Render';

const columns = [
  {
    field: 'key',
    headerName: 'Key',
    description: 'Custom property key',
    type: 'string',
    flex: 1,
  },
  {
    field: 'name',
    headerName: 'Name',
    description: 'Custom property display name',
    type: 'string',
    flex: 1,
  },
  {
    field: 'inheritanceKey',
    headerName: 'Inheritance Key',
    description:
      'Custom property inheritance key used to match custom properties when propagating values between records',
    type: 'string',
    flex: 1,
  },
  {
    field: 'model',
    headerName: 'Data Type',
    description:
      'Associated model for the custom property. This determines where the custom property is available',
    type: 'string',
    flex: 1,
    valueGetter: (params: GridValueGetterParams) =>
      _.get(CustomPropertyModelDisplay, params.row.model, ''),
  },
  {
    field: 'dataType',
    headerName: 'Data Type',
    description: 'Data type of the custom property',
    type: 'string',
    flex: 1,
    valueGetter: (params: GridValueGetterParams) =>
      _.get(CustomPropertyDataTypeDisplay, params.row.dataType, ''),
  },
  {
    field: 'visibility',
    headerName: 'Visibility',
    description: 'Custom property visibility',
    type: 'string',
    flex: 1,
    valueGetter: (params: GridValueGetterParams) =>
      _.chain(params.row.visibility)
        .map((v) => _.get(CustomPropertyVisibilityDisplay, v, ''))
        .join(', ')
        .value(),
    renderCell: longTextWithEllipsisRenderCell,
  },
  {
    field: 'defaultValue',
    headerName: 'Default Value',
    description: 'Default value for the custom property',
    type: 'string',
    flex: 1,
  },
  {
    field: 'nullable',
    headerName: 'Nullable',
    description: 'Whether the custom property can have a null value',
    type: 'boolean',
    flex: 0.5,
  },
  {
    field: 'values',
    headerName: 'Values',
    description: 'Value options for Single/Multiple Choice data types',
    type: 'string',
    flex: 1,
    valueGetter: (params: GridValueGetterParams) =>
      _.chain(params.row.values)
        .map((v) => `${v.value}: ${v.label}`)
        .join(', ')
        .value(),
    renderCell: longTextWithEllipsisRenderCell,
  },
];

const CustomPropertiesTable = () => {
  const { data: customProperties, isLoading: isLoadingCustomProperties } =
    useGetCustomPropertiesQuery();

  return (
    <>
      <Alert severity="info" sx={{ m: gridSpacing }}>
        <AlertTitle>Contact Success</AlertTitle>
        To add or modify the Custom Properties for your organization contact
        Cofactr Success or your implementation team by emailing
        success@cofactr.com or using the in-platform chat in the lower right
        hand corner of this screen.
      </Alert>
      <ExtendedDatagrid
        loading={isLoadingCustomProperties}
        noRows="No Custom Properties found."
        gridName={DatagridNames.customProperties}
        rows={customProperties ?? []}
        columns={columns}
        initialVisibleColumns={[
          'key',
          'name',
          'inheritanceKey',
          'model',
          'dataType',
          'defaultValue',
        ]}
      />
    </>
  );
};

export default CustomPropertiesTable;
